import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, LogOut, User } from "lucide-react";
import api from "@/lib/api";
import { useAuth, ROLES } from "@/lib/auth";
import { fmtDateTime } from "@/lib/format";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export default function Topbar({ title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [notifs, setNotifs] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.count || 0);
      const list = await api.get("/notifications");
      setNotifs(list.data.slice(0, 6));
    } catch {}
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const markAllRead = async () => {
    try {
      await api.patch("/notifications/read-all");
      await load();
    } catch {}
  };

  return (
    <header
      data-testid="topbar"
      className="bg-white border-b border-[#E5E7EB] sticky top-0 z-30"
    >
      <div className="px-5 lg:px-8 py-3 flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-[#08285A] tracking-tight truncate">
            {title || "Dashboard"}
          </h1>
          <p className="text-xs text-[#6B7280] font-medium">
            AZD Flow • Şirket İçi Yönetim Sistemi
          </p>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="topbar-notifications"
              className="relative p-2 rounded-lg hover:bg-[#F6F8FB] transition-colors"
              aria-label="Bildirimler"
            >
              <Bell size={20} className="text-[#08285A]" />
              {unread > 0 && (
                <span
                  data-testid="notification-badge"
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center"
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <div className="flex items-center justify-between px-2 py-1.5">
              <DropdownMenuLabel className="text-sm font-semibold p-0">
                Bildirimler
              </DropdownMenuLabel>
              <button
                onClick={markAllRead}
                data-testid="mark-all-read"
                className="text-xs text-[#0B3676] hover:underline font-medium"
              >
                Tümünü okundu yap
              </button>
            </div>
            <DropdownMenuSeparator />
            {notifs.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-[#6B7280]">
                Bildirim bulunmuyor
              </div>
            ) : (
              notifs.map((n) => (
                <div
                  key={n.id}
                  className={`px-3 py-2 border-b border-[#E5E7EB] last:border-0 ${
                    !n.is_read ? "bg-[#FFE600]/5" : ""
                  }`}
                >
                  <div className="text-[13px] font-semibold text-[#111827]">{n.title}</div>
                  <div className="text-[12px] text-[#6B7280] mt-0.5">{n.message}</div>
                  <div className="text-[10px] text-[#6B7280] mt-1">
                    {fmtDateTime(n.created_at)}
                  </div>
                </div>
              ))
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/notifications")} data-testid="all-notifications">
              Tüm bildirimleri gör
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="topbar-user"
              className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg hover:bg-[#F6F8FB] transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-[#08285A] text-white flex items-center justify-center text-sm font-bold">
                {user?.full_name?.[0] || "A"}
              </div>
              <div className="hidden sm:block text-left">
                <div className="text-sm font-semibold text-[#111827] leading-tight">
                  {user?.full_name}
                </div>
                <div className="text-[11px] text-[#6B7280] leading-tight">
                  {ROLES[user?.role] || user?.role}
                </div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="text-sm font-semibold">{user?.full_name}</div>
              <div className="text-xs text-[#6B7280] font-normal">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
              <User size={14} className="mr-2" /> Profilim
            </DropdownMenuItem>
            <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-[#DC2626]">
              <LogOut size={14} className="mr-2" /> Çıkış Yap
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
